import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { CheckIcon, Loader2Icon } from "lucide-react";
import type {
  HLDWizardContent,
  HLDWizardResponse,
  DecisionContent,
  DecisionAnswer,
  ChooseJustifyAnswer,
  ChooseChecklistAnswer,
  ChooseAdaptiveJustifyAnswer,
  OrderingAnswer,
  ChooseSignalsScenarioAnswer,
} from "@/types/hld-wizard";
import { getDecisionType } from "@/types/hld-wizard";
import type { DraftStatus } from "@/lib/hooks/use-draft-auto-save";
import { DecisionStepper } from "./decision-stepper";
import { ScenarioPanel } from "./scenario-panel";
import {
  ChooseJustifyDecision,
  ChooseChecklistDecision,
  ChooseAdaptiveJustifyDecision,
  OrderingDecision,
  ChooseSignalsScenarioDecision,
} from "./decisions";

interface HLDWizardInputProps {
  content: HLDWizardContent;
  value: HLDWizardResponse | null;
  onChange: (value: HLDWizardResponse) => void;
  onSubmit: () => void;
  isSubmitting?: boolean;
  disabled?: boolean;
  draftStatus?: DraftStatus;
}

function isDecisionComplete(
  decision: DecisionContent,
  answer: DecisionAnswer | undefined,
): boolean {
  if (!answer) return false;
  const type = getDecisionType(decision);

  switch (type) {
    case "choose_justify": {
      const a = answer.choose_justify;
      return !!(
        a &&
        a.option_id &&
        a.justification_ids.length > 0 &&
        a.downside_text.length > 10
      );
    }
    case "choose_checklist": {
      const a = answer.choose_checklist;
      return !!(a && a.option_id && a.checked_ids.length > 0);
    }
    case "choose_adaptive_justify": {
      const a = answer.choose_adaptive_justify;
      if (!a || !a.option_id || !a.secondary_option_id) return false;
      if (a.secondary_justify_text.length <= 10) return false;
      if (decision.choose_adaptive_justify?.tertiary && !a.tertiary_option_id)
        return false;
      return true;
    }
    case "ordering": {
      const a = answer.ordering;
      return !!(
        a &&
        a.order.length > 0 &&
        a.followup_option_id &&
        a.followup_justify_text.length > 5
      );
    }
    case "choose_signals_scenario": {
      const a = answer.choose_signals_scenario;
      const maxSig =
        decision.choose_signals_scenario?.signals.max_selections ?? 2;
      return !!(
        a &&
        a.option_id &&
        a.signal_ids.length === maxSig &&
        a.scenario_option_id
      );
    }
    default:
      return false;
  }
}

// Exported for use in AssessmentTake.tsx canSubmit check
export function isHLDWizardAllComplete(
  value: HLDWizardResponse | null,
  content: HLDWizardContent,
): boolean {
  if (!value) return false;
  return content.decisions.every((d) =>
    isDecisionComplete(d, value.decisions[d.id]),
  );
}

export function HLDWizardInput({
  content,
  value,
  onChange,
  onSubmit,
  isSubmitting,
  disabled,
  draftStatus,
}: HLDWizardInputProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [scenarioCollapsed, setScenarioCollapsed] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const decisions = content.decisions;
  const currentDecision = decisions[currentStep];
  const response: HLDWizardResponse = value ?? { decisions: {} };

  const completedSteps = useMemo(() => {
    const set = new Set<number>();
    decisions.forEach((d, i) => {
      if (isDecisionComplete(d, response.decisions[d.id])) {
        set.add(i);
      }
    });
    return set;
  }, [decisions, response]);

  const canAdvance = isDecisionComplete(
    currentDecision,
    response.decisions[currentDecision.id],
  );

  const isLastStep = currentStep === decisions.length - 1;

  const allComplete = decisions.every((d) =>
    isDecisionComplete(d, response.decisions[d.id]),
  );

  // Auto-collapse scenario after step 2
  useEffect(() => {
    if (currentStep >= 2 && !scenarioCollapsed) {
      setScenarioCollapsed(true);
    }
  }, [currentStep]);

  // Scroll to top of wizard content on step change
  useEffect(() => {
    contentRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [currentStep]);

  const updateDecisionAnswer = useCallback(
    (decisionId: string, answer: DecisionAnswer) => {
      onChange({
        ...response,
        decisions: {
          ...response.decisions,
          [decisionId]: answer,
        },
      });
    },
    [response, onChange],
  );

  function handleNext() {
    if (currentStep < decisions.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  }

  function handleBack() {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  }

  return (
    <div ref={contentRef} className="space-y-5">
      <DecisionStepper
        decisions={decisions}
        currentStep={currentStep}
        completedSteps={completedSteps}
        onNavigate={setCurrentStep}
      />

      <ScenarioPanel
        scenario={content.scenario}
        collapsed={scenarioCollapsed}
        onToggle={() => setScenarioCollapsed(!scenarioCollapsed)}
      />

      <div>
        <div className="flex items-center gap-3 mb-1">
          <span className="rounded bg-muted px-2 py-0.5 text-xs font-semibold">
            Decision {currentStep + 1}
          </span>
          <span className="text-xs text-muted-foreground">
            {currentDecision.points} pts
          </span>
        </div>
        <h3 className="text-xl font-semibold leading-[1.35]">
          {currentDecision.title}
        </h3>
      </div>

      <DecisionRenderer
        decision={currentDecision}
        answer={response.decisions[currentDecision.id]}
        onAnswerChange={(a) =>
          updateDecisionAnswer(currentDecision.id, a)
        }
        disabled={disabled}
      />

      {/* Wizard navigation — owns the bottom bar while active */}
      {!disabled && (
        <>
          <div className="flex items-center justify-between pt-4">
            <div>
              {currentStep > 0 && (
                <Button variant="outline" size="lg" onClick={handleBack}>
                  ← Back Decision
                </Button>
              )}
            </div>

            <div className="text-xs text-muted-foreground">
              {draftStatus === "saving" && (
                <span className="flex items-center gap-1">
                  <Loader2Icon className="h-3 w-3 animate-spin" />
                  saving...
                </span>
              )}
              {draftStatus === "saved" && (
                <span className="flex items-center gap-1">
                  <CheckIcon className="h-3 w-3" />
                  auto-saved
                </span>
              )}
            </div>

            <div>
              {isLastStep ? (
                <Button
                  size="lg"
                  onClick={onSubmit}
                  disabled={!allComplete || isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2Icon className="h-4 w-4 animate-spin" />
                      Evaluating...
                    </>
                  ) : (
                    "Submit Design"
                  )}
                </Button>
              ) : (
                <Button
                  size="lg"
                  onClick={handleNext}
                  disabled={!canAdvance}
                >
                  Next Decision →
                </Button>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// Decision renderer — dispatches to the correct decision component
function DecisionRenderer({
  decision,
  answer,
  onAnswerChange,
  disabled,
}: {
  decision: DecisionContent;
  answer: DecisionAnswer | undefined;
  onAnswerChange: (answer: DecisionAnswer) => void;
  disabled?: boolean;
}) {
  const type = getDecisionType(decision);

  switch (type) {
    case "choose_justify":
      return (
        <ChooseJustifyDecision
          spec={decision.choose_justify!}
          context={decision.context}
          question={decision.question}
          answer={answer?.choose_justify}
          onChange={(a: ChooseJustifyAnswer) =>
            onAnswerChange({ choose_justify: a })
          }
          disabled={disabled}
        />
      );
    case "choose_checklist":
      return (
        <ChooseChecklistDecision
          spec={decision.choose_checklist!}
          context={decision.context}
          question={decision.question}
          answer={answer?.choose_checklist}
          onChange={(a: ChooseChecklistAnswer) =>
            onAnswerChange({ choose_checklist: a })
          }
          disabled={disabled}
        />
      );
    case "choose_adaptive_justify":
      return (
        <ChooseAdaptiveJustifyDecision
          spec={decision.choose_adaptive_justify!}
          context={decision.context}
          question={decision.question}
          answer={answer?.choose_adaptive_justify}
          onChange={(a: ChooseAdaptiveJustifyAnswer) =>
            onAnswerChange({ choose_adaptive_justify: a })
          }
          disabled={disabled}
        />
      );
    case "ordering":
      return (
        <OrderingDecision
          spec={decision.ordering!}
          context={decision.context}
          question={decision.question}
          answer={answer?.ordering}
          onChange={(a: OrderingAnswer) => onAnswerChange({ ordering: a })}
          disabled={disabled}
        />
      );
    case "choose_signals_scenario":
      return (
        <ChooseSignalsScenarioDecision
          spec={decision.choose_signals_scenario!}
          context={decision.context}
          question={decision.question}
          answer={answer?.choose_signals_scenario}
          onChange={(a: ChooseSignalsScenarioAnswer) =>
            onAnswerChange({ choose_signals_scenario: a })
          }
          disabled={disabled}
        />
      );
  }
}
